// Evaluador de formulas y condiciones "limpiar_formula" en el cliente.
// Replica calcular_formula_normal + get_operation_number del backend Ruby
// (app/helpers/formatos/update_answers_helper.rb) para feedback en vivo
// mientras el usuario llena el survey.

// Extrae los aliases referenciados en una formula tipo "{A} + {B} * 2".
export const extractAliases = (formula) => {
  if (!formula) return [];
  const matches = formula.match(/\{([.\w\s]*)\}/g) || [];
  return matches.map((m) => m.replace(/[{}]/g, ""));
};

// Convierte un valor (numero, fecha, hora) al numero usado por la formula.
const coerceFormulaValue = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") return 0;
  const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
  if (value === null || value === undefined || value === "") return 0;

  const str = String(value);
  const num = parseFloat(str);
  if (!isNaN(num) && /^-?\d+(\.\d+)?$/.test(str.trim())) return num;

  return num || 0;
};

// Evalua la formula con los valores actuales del formulario.
// formula: string como "{A} + {B}"
// valuesByAlias: { ALIAS_NAME: rawValue }
// Devuelve un numero o null si hay error.
export const evaluateFormula = (formula, valuesByAlias) => {
  if (!formula) return null;
  let expr = formula;
  const aliases = extractAliases(formula);
  aliases.forEach((aliasName) => {
    const value = coerceFormulaValue(valuesByAlias[aliasName]);
    expr = expr.split(`{${aliasName}}`).join(String(value));
  });

  if (!/^[\d\s+\-*/().]+$/.test(expr)) return null;

  try {
    return Function(`"use strict"; return (${expr});`)();
  } catch (_e) {
    return null;
  }
};

// Evalua una sola condicional contra el valor de la formula.
const checkOperation = (operation, valueReal, valueTarget) => {
  const a = parseFloat(valueReal);
  const b = parseFloat(valueTarget);
  if (isNaN(a) || isNaN(b)) return false;
  switch (operation) {
    case "Igual": return a === b;
    case "Diferente": return a !== b;
    case "Mayor": return a > b;
    case "Mayor e igual": return a >= b;
    case "Menor": return a < b;
    case "Menor e igual": return a <= b;
    default: return false;
  }
};

// Evalua una lista de condicionales con AND/OR (campo conditional: "Y" / "O").
// Devuelve true si la accion debe dispararse.
export const evaluateConditionals = (conditionals, formulaResult) => {
  if (!Array.isArray(conditionals) || conditionals.length === 0) return false;
  let acc = false;
  conditionals.forEach((cond, index) => {
    const passes = checkOperation(cond.operation, formulaResult, cond.value);
    if (index === 0) {
      acc = passes;
    } else if (cond.conditional === "O") {
      acc = acc || passes;
    } else {
      acc = acc && passes;
    }
  });
  return acc;
};

// Dado un question (con formula_clear_actions) y los valores actuales
// indexados por alias, devuelve los question_ids cuyos campos deben
// limpiarse, o [] si ninguna accion dispara.
export const getFormulaClearIds = (question, valuesByAlias) => {
  if (!question || question.question_type !== "answer_formula") return [];
  const actions = question.formula_clear_actions || [];
  if (actions.length === 0) return [];

  // No disparar la condicion hasta que TODOS los campos referenciados por la
  // formula tengan valor. Asi se espera a que el usuario termine de llenar
  // ambos operandos en vez de evaluar con una suma parcial (un campo vacio se
  // trataria como 0 y dispararia el error prematuramente).
  const aliases = extractAliases(question.answer_formula);
  const allFilled = aliases.every((aliasName) => {
    const raw = valuesByAlias[aliasName];
    const v = Array.isArray(raw) ? raw[0] : raw;
    return v !== null && v !== undefined && String(v).trim() !== "";
  });
  if (aliases.length > 0 && !allFilled) return [];

  const result = evaluateFormula(question.answer_formula, valuesByAlias);
  if (result === null) return [];

  for (const action of actions) {
    if (evaluateConditionals(action.conditionals, result)) {
      return action.alias_question_ids || [];
    }
  }
  return [];
};
